<template>
  <base-view title="Integrar produtos - Marketplaces" class="mb-5 mr-4">
    <b-row class="mb-5">
      <b-col cols="12" class="mb-3">
        <div class="mb-2">Selecione o marketplace que deseja integrar:</div>

        <b-alert show class="fs-14">
          <i class="fas fa-info-circle mr-1"></i> <b>Observação:</b> Só é
          possível realizar a integração dos produtos selecionados com um
          marketplace por vez, pois cada plataforma possui um processo de
          integração diferente.
        </b-alert>
      </b-col>
      <b-col
        cols="12"
        class="d-flex align-items-center"
        v-if="loadingEcommerces"
      >
        <b-spinner small class="mr-2" /> Carregando marketplaces
      </b-col>
      <b-col cols="12" class="d-flex align-items-center">
        <marketplace-select-card
          v-for="marketplace in marketplaces"
          :key="marketplace.id"
          :marketplace="marketplace"
          :selectedMarketplace="selectedMarketplace"
          @select="handleECommerceSelection(marketplace)"
          class="mr-4"
        />
      </b-col>
    </b-row>
    <b-row
      align-content="center"
      align-v="center"
      v-if="selectedMarketplace.id"
    >
      <b-col cols="12">
        <b-input
          placeholder="Pesquise pelo nome dos produtos"
          debounce="1000"
          v-model="searchFields.productName"
          @keydown.enter="getProducts"
        >
        </b-input>
      </b-col>
      <b-col cols="12" class="mt-2 mb-4 d-flex align-items-center">
        <div>
          <b-button
            :disabled="integrating"
            variant="success"
            @click="integrateProducts()"
            class="mr-2"
          >
            <i class="fas fa-file-import mr-2"></i>Integrar
          </b-button>
          <b-button
            :disabled="integrating"
            variant="warning"
            @click="selectedProducts = []"
          >
            <i class="fas fa-trash-alt mr-2"></i>Limpar seleção
          </b-button>
        </div>
        <b-badge class="ml-auto"
          >{{ selectedProducts.length }} produtos selecionados</b-badge
        >
      </b-col>
      <b-col cols="12" v-if="!loading && (!products || !products.length)">
        <b-card class="mx-auto" style="max-width: 220px">
          <b-card-text
            class="d-flex flex-column align-items-center text-center"
          >
            <i class="fas fa-info-circle fs-20 text-info mb-2"></i>
            Sem produtos encontrados
          </b-card-text>
        </b-card>
      </b-col>
      <b-col cols="12" v-if="Object.keys(selectedMarketplace).length">
        <label>Aumentar preço dos produtos</label>
        <input
          type="text"
          class="form-control"
          v-model="add.value"
          v-money="money"
          @blur="updatePrice()"
        />
        <input
          type="radio"
          name="add_price"
          value="percentage"
          v-model="add.type"
          @change="updatePrice()"
        />
        <label class="mr-3 ml-1">Porcentagem</label>
        <input
          type="radio"
          name="add_price"
          value="price"
          v-model="add.type"
          @change="updatePrice()"
        />
        <label class="ml-1">Valor R$</label>
      </b-col>
      <b-col v-if="Object.keys(selectedMarketplace).length">
        <component
          :is="marketplaceIntegrationScreen"
          :products="products"
          :selectedProducts="selectedProducts"
          :integrating="integrating"
          @select-product="handleProductSelection"
          @update-details="updateProductDetails"
        />
      </b-col>
      <b-col
        v-if="loading"
        cols="12"
        class="d-flex flex-column align-items-center mb-4"
      >
        <b-spinner label="Spinning" class="mb-2"> </b-spinner>
        Carregando seus produtos...
      </b-col>
      <b-col cols="12" v-if="!reachLastPage">
        <div v-observe-visibility="visibilityChanged"></div>
      </b-col>
    </b-row>
  </base-view>
</template>

<script>
import { mapActions, mapState } from 'vuex';
import shopkeeperAppApi from '@/api/shopkeeperAppApi';
import makeToast from '@/mixins/makeToast';
import UserTypes from '@/enums/UserTypes';
import EnabledIntegrationStatus from 'Enums/Integration/EnabledIntegrationStatus';
import IntegrationTypes from 'Enums/Integration/IntegrationTypes';

import MarketplaceSelectCard from './_components/MarketplaceSelectCard.vue';
import MarketplaceMapFolder from './_components/map-folder';

export default {
  name: 'IntegrateProducts',
  mixins: [makeToast],
  components: {
    MarketplaceSelectCard,
  },
  data() {
    return {
      EnabledIntegrationStatus,
      IntegrationTypes,
      loading: false,
      loadingEcommerces: false,
      integrating: false,
      add: {
        value: null,
        type: 'percentage',
      },
      money: {
        decimal: ',',
        thousands: '.',
        prefix: '',
        suffix: '',
        precision: 2,
        masked: true,
      },
      pagination: {
        currentPage: 0,
        itemsPerPage: 10,
        total: 0,
        lastPage: 0,
      },
      searchFields: {
        productName: '',
      },
      selectedMarketplace: {},
      selectedProducts: [],
      products: [],
      marketplaces: [],
      productsAlreadyIntegrated: [],
    };
  },
  computed: {
    ...mapState(['dadosUsuario']),
    reachLastPage() {
      return (
        this.pagination.lastPage > 0 &&
        this.pagination.currentPage >= this.pagination.lastPage
      );
    },
    marketplaceIntegrationScreen() {
      if (!this.selectedMarketplace || !this.selectedMarketplace.type) return;

      const folder = MarketplaceMapFolder[this.selectedMarketplace.type];

      return () => import(`./_components/${folder}/Integrate.vue`);
    },
  },
  watch: {
    'searchFields.productName': function () {
      this.pagination = {
        currentPage: 1,
        itemsPerPage: 10,
        total: 0,
        lastPage: 0,
      };
      this.products = [];
      this.getProducts();
    },
    'pagination.currentPage': function () {
      this.getProducts();
    },
    'selectedMarketplace.id': function () {
      if (!this.selectedMarketplace.id) return;

      this.pagination = {
        currentPage: 1,
        itemsPerPage: 10,
        total: 0,
        lastPage: 0,
      };
      this.products = [];
      this.getProducts();
    },
  },
  mounted() {
    this.getEcommerces();
  },
  methods: {
    ...mapActions('shopkeeper', ['getSelectedProducts']),
    async getEcommerces() {
      this.loadingEcommerces = true;

      const { data } = await shopkeeperAppApi.get(
        `/integration/marketplace/${this.userTypeId()}/integrations`
      );

      this.loadingEcommerces = false;

      this.marketplaces = data.data || [];
    },
    updatePrice() {
      this.products = this.products.map((product) => {
        const basePrice = parseFloat(product.priceSalesFormat1) || 0;

        const value =
          parseFloat(this.add.value.replace(/\./g, '').replace(',', '.')) || 0;
        let newPrice = basePrice;

        if (this.add.type === 'percentage') {
          newPrice = basePrice + (basePrice * value) / 100;
        } else if (this.add.type === 'price') {
          newPrice = basePrice + value;
        }

        return {
          ...product,
          priceSalesFormatNew: newPrice.toFixed(2),
        };
      });
    },
    userTypeId() {
      return this.$route.meta.userType == UserTypes.BALCONISTA
        ? this.dadosUsuario.construp_user_id
        : this.dadosUsuario.id;
    },
    getProducts() {
      if (this.loading) return;

      this.loading = true;

      this.getSelectedProducts({
        userId: this.userTypeId(),
        productName: this.searchFields.productName,
        marketplaceId: this.selectedMarketplace.id,
        integrated: false,
        currentPage: this.pagination.currentPage,
        status: 0,
      })
        .then((response) => {
          const { data, perPage, total, lastPage } = response.data.data || {};
          this.pagination.itemsPerPage = perPage || 15;
          this.pagination.total = total;
          this.pagination.lastPage = lastPage;
          this.products.push(...(data || []));

          this.filterProductsIntegrated();
        })
        .finally(() => {
          this.loading = false;
        });
    },
    async integrateProducts() {
      try {
        this.integrating = true;

        const { data } = await shopkeeperAppApi.post(
          `/integration/marketplace/publish-products?integrationType=${
            IntegrationTypes.MERCADO_LIVRE
          }&construpUserId=${this.userTypeId()}`,
          {
            products: this.selectedProducts || [],
          }
        );

        this.makeToast(
          'Sucesso',
          'Os produtos foram adicionados a fila de integração com sucesso'
        );

        this.products = this.products.filter(
          (p) =>
            !this.selectedProducts.some((pi) => pi.productId === p.productId)
        );
        this.selectedProducts = [];
      } catch (ex) {
        this.makeToast(
          'Erro',
          `Houve um erro ao realizar a integração: ${ex.message}`,
          'danger'
        );
      } finally {
        this.integrating = false;
      }
    },
    async getMarketplaceProductIntegrations() {
      const query = `?construpUserId=${this.userTypeId()}&integrationType=${
        this.selectedMarketplace.type
      }`;
      const { data } = await shopkeeperAppApi.get(
        `/integration/marketplace/products-integrations${query}`
      );

      if (!data.data) return;

      this.productsAlreadyIntegrated = data.data || [];
      this.filterProductsIntegrated();
    },
    handleProductSelection(newProduct) {
      if (
        this.selectedProducts.some((p) => p.productId === newProduct.productId)
      ) {
        this.selectedProducts = this.selectedProducts.filter(
          (p) => p.productId !== newProduct.productId
        );
        return;
      }

      this.selectedProducts.push(newProduct);
    },
    updateProductDetails(newProduct) {
      if (
        this.selectedProducts.some((p) => p.productId === newProduct.productId)
      ) {
        this.selectedProducts = this.selectedProducts.filter(
          (p) => p.productId !== newProduct.productId
        );
      }

      this.selectedProducts.push(newProduct);
    },
    handleECommerceSelection(marketplace) {
      if (!marketplace.enabledIntegration) return;

      if (
        marketplace.enabledIntegration.status !==
        EnabledIntegrationStatus.ACTIVE
      )
        return;

      if (this.selectedMarketplace.id === marketplace.id) {
        this.selectedMarketplace = {};
        return;
      }

      this.selectedMarketplace = marketplace;
    },
    visibilityChanged(isVisible) {
      if (!isVisible || this.loading) return;

      if (this.reachLastPage) return;

      this.pagination.currentPage = this.pagination.currentPage + 1;
    },
    removeProduct(product) {
      this.products = this.products.filter(
        (p) => p.productId !== product.productId
      );
      this.selectedProducts = this.selectedProducts.filter(
        (p) => p.productId !== product.productId
      );
    },
    filterProductsIntegrated() {
      this.products = this.products.filter(
        (p) =>
          !this.productsAlreadyIntegrated.some(
            (pi) => pi.productId === p.productId
          )
      );
    },
  },
};
</script>
